// TextfieldWithPeriod
import React, { useState, useEffect, useMemo } from "react";
import validator from "validator";

import { Box, Card } from "@metrea-ui/v2";
import { NumericFormatAdapter } from "../common/NumericFormatAdapter";
import { Field, Input } from "@metrea-ui/v2";
import styles from "./TextfieldWithPeriod.module.css";

import { mColor } from "src/utils/themeUtils";
import { debugLog } from "src/utils/debugUtils";
import { findObjectByProperty, getValuesByPropertyPath, deepClone } from "src/utils/dataUtils";

const TextfieldWithPeriod = (props) => {
	const {
		item,
		currencyDisplayDecimal,
		type,
		resetKey,
		onValidateData,
		periodLabel,
		onClearComplete,
		onUpdate,
		currencySymbol,
		currencyUnit,
	} = props;
	const [currPeriods, setCurrPeriods] = useState(deepClone(item));
	const [dataError, setDataError] = useState("");
	const [lastValidationMeta, setLastValidationMeta] = useState(null);
	const decimals = Number.isInteger(Number(currencyDisplayDecimal)) ? Number(currencyDisplayDecimal) : 2;
	const usesDefaultPeriodLabels = type?.code === "period_label";

	// Safety guard — some renders pass null during new-item creation.
	// Ensure currPeriods is ALWAYS an array to prevent .map() crashes.
	const safePeriods = useMemo(() => (Array.isArray(currPeriods) ? currPeriods : []), [currPeriods]);
	const hasMissingRequiredValues = safePeriods.some((cell) => `${cell?.comp_value ?? ""}`.trim() === "");
	const sectionInvalid = dataError !== "" || hasMissingRequiredValues;

	useEffect(() => {
		setCurrPeriods(deepClone(item));

		// debugLog(10, "[TextfieldWithPeriod] Props.item changes!!!!!!!!!!!!");
	}, [item]);

	useEffect(() => {
		// Only runs when resetKey changes (i.e., when "Revert Data" is clicked)
		setCurrPeriods(deepClone(item));
		setDataError("");
	}, [item, resetKey]);

	useEffect(() => {
		if (typeof onValidateData === "function") {
			const hasError = dataError !== "" || hasMissingRequiredValues;
			const firstMissingIndex = safePeriods.findIndex(
				(cell) => `${cell?.comp_value ?? ""}`.trim() === "",
			);
			const firstMissingCell = firstMissingIndex >= 0 ? safePeriods[firstMissingIndex] : null;
			const currentPeriodLabel = periodLabel?.[firstMissingIndex]?.comp_value?.trim();
			const firstMissingField =
				firstMissingIndex >= 0
					? {
							id: firstMissingCell?.id ?? null,
							index: firstMissingIndex,
							label: usesDefaultPeriodLabels
								? `Period ${firstMissingIndex + 1}`
								: currentPeriodLabel || `Period ${firstMissingIndex + 1}`,
						}
					: null;

			onValidateData({
				hasError,
				cfgCode: type?.code,
				reason: dataError !== "" ? "format" : hasMissingRequiredValues ? "required" : null,
				errorMessage: dataError,
				lastValidationMeta,
				firstMissingField,
			});
		}
	}, [
		dataError,
		hasMissingRequiredValues,
		lastValidationMeta,
		onValidateData,
		periodLabel,
		type?.code,
		usesDefaultPeriodLabels,
		safePeriods,
	]);

	const verifyDataFields = (pros, value) => {
		let errorFound = false;

		if (type.format === "currency") {
			setDataError(
				value &&
					value.trim() &&
					validator.isDecimal(value.trim()) &&
					validator.isFloat(value.trim(), { min: 0 })
					? ""
					: "Values must be greater than or equal to 0",
			);
		} else {
			setDataError(value && value.trim() ? "" : "Value is required. It can't be empty.");
		}

		return !errorFound;
	};

	const handleInputChange = (option) => {
		const { name, value } = option.target;
		const { logicId } = option;
		const target = JSON.parse(logicId);

		// Clone before making changes
		const periodsClone = deepClone(currPeriods);

		const cfg = findObjectByProperty(periodsClone, "id", target.id);
		const thisIndex = Array.isArray(periodsClone)
			? periodsClone.findIndex((x) => x?.id === target.id)
			: -1;
		const thisLabel =
			thisIndex >= 0 ? periodLabel?.[thisIndex]?.comp_value?.trim() || `Period ${thisIndex + 1}` : null;
		setLastValidationMeta({
			id: target.id ?? null,
			index: thisIndex,
			label: thisLabel,
			value: value,
			cfgCode: type?.code,
		});

		debugLog(7, "[TextfieldWithPeriod][ACTION] handleInputChange", {
			option: option,
			name: name,
			value: value,
			periodsClone: periodsClone,
		});

		if (type.format === "currency") {
			setDataError(
				value &&
					value.trim() &&
					validator.isDecimal(value.trim()) &&
					validator.isFloat(value.trim(), { min: 0 })
					? ""
					: "Values must be greater than or equal to 0",
			);
		} else {
			setDataError(value && value.trim() ? "" : "Value is required. It can't be empty.");
		}
		verifyDataFields(props, value);

		if (cfg && cfg.comp_value !== value) {
			cfg.comp_value = value;
			cfg.update_flag = 102; // UPDATE
			setCurrPeriods(periodsClone); // Update state with the clone
		}

		onClearComplete();
	};

	const blurHandler = (event) => {
		const arrayStatus = getValuesByPropertyPath(currPeriods, "update_flag");
		if (arrayStatus.indexOf(102) !== -1) {
			// Save to DB
			onUpdate([...currPeriods], `cfg_${type.code}`);

			setCurrPeriods([...currPeriods]);
		}

		// debugLog(10, "[TextfieldWithPeriod] blurHandler", {
		// 	arrayStatus: arrayStatus,
		// 	currPeriods: currPeriods,
		// });
	};

	// debugLog(10, "[TextfieldWithPeriod]", { props: props });

	// Keep this helper local because the period-grid editor still owns its
	// per-cell validation, blur-save behavior, and numeric formatting rules.
	const renderPeriodField = ({
		id,
		label,
		value,
		onChange,
		onBlur,
		disabled = false,
		invalid = false,
		formatSpec,
		isNumeric = false,
	}) => {
		return (
			<Field.Root required invalid={invalid} style={{ width: "100%" }}>
				<Field.Label htmlFor={id}>
					<div className={styles.fieldLabel}>{label}</div>
					<Field.RequiredIndicator />
				</Field.Label>
				{isNumeric ? (
					<NumericFormatAdapter
						customInput={Input}
						id={id}
						name={id}
						value={value ?? ""}
						disabled={disabled}
						size="sm"
						variant="subtle"
						aria-invalid={invalid ? "true" : undefined}
						onChange={onChange}
						onBlur={onBlur}
						formatSpec={formatSpec}
						style={{
							width: "100%",
							fontSize: "0.8rem",
						}}
					/>
				) : (
					<Input
						id={id}
						name={id}
						value={value ?? ""}
						disabled={disabled}
						aria-invalid={invalid ? "true" : undefined}
						onChange={onChange}
						onBlur={onBlur}
						size="sm"
						variant="subtle"
						style={{
							width: "100%",
							fontSize: "0.8rem",
						}}
					/>
				)}
			</Field.Root>
		);
	};

	return (
		<div>
			{/* Keep period sections visually aligned with chip sections after wrapper drain. */}
			<Card.Root variant="elevated">
				<Card.Body padding="1rem">
					<Box as="form" noValidate autoComplete="off">
						<div
							className={styles.configLabel}
							style={sectionInvalid ? { color: mColor.Danger } : undefined}
						>
							{type.name}
							{props.requiredSectionLabel ? (
								<span style={{ color: mColor.Danger, marginLeft: 4 }}>*</span>
							) : null}
						</div>
						<div>
							{sectionInvalid && (
								<div
									style={{
										paddingLeft: "10px",
										paddingBottom: "10px",
										color: mColor.Danger,
										fontSize: "0.8rem",
									}}
								>
									{dataError !== "" ? dataError : "Please enter values for all fields."}
								</div>
							)}
						</div>
						<div className={styles.configGroup}>
							{safePeriods.map((cell, index) => {
								// JSON string used as id/name breaks label[for] selector → browser SyntaxError.
								// Keep original for reference:
								// id={JSON.stringify(thisItem)}
								// name={JSON.stringify(thisItem)}
								//
								//  use a safe, stable identifier using only cfgCategory + id.
								const safeId = `${type.code}_${cell.id}`;
								// "Labels for each period" is the source-of-truth editor for period names,
								// so that section should always show stable Period N labels instead of echoing its own values.
								const strLabel = usesDefaultPeriodLabels
									? `Period ${index + 1}`
									: periodLabel[index] && periodLabel[index].comp_value !== ""
										? periodLabel[index].comp_value
										: `Period ${index + 1}`;
								const thisItem = {
									cfgCategory: type.code,
									type_id: cell.comp_type_id,
									format: type.format,
									id: cell ? cell.id : 0,
									value: cell?.comp_value ?? "",
									unit: currencyUnit ?? "",
								};
								// Keep required blanks highlighted on initial load, but scope format
								// errors to the last field that actually failed validation.
								const hasMissingValue = `${thisItem.value ?? ""}`.trim() === "";
								const hasFormatError =
									dataError !== "" && lastValidationMeta?.id === thisItem.id;
								const isCellInvalid = hasMissingValue || hasFormatError;
								const isNumeric =
									type.format === "currency" ||
									type.format === "percentage" ||
									type.format === "integer";
								const formatSpec =
									type.format === "currency"
										? {
												kind: "currency",
												symbol: currencySymbol || "",
												unit: currencyUnit || "",
												decimals,
												fixed: true,
												allowNegative: false,
											}
										: type.format === "percentage"
											? {
													kind: "percentage",
													decimals: 2,
													fixed: true,
													allowNegative: false,
												}
											: type.format === "integer"
												? {
														kind: "number",
														decimals: 0,
														fixed: false,
														allowNegative: false,
													}
												: {
														kind: "number",
														decimals: 0,
														fixed: false,
														allowNegative: false,
													};
								return (
									<div key={index} className={styles.configItem}>
										{renderPeriodField({
											id: safeId,
											label: strLabel,
											value: thisItem.value,
											onChange: (opt) =>
												handleInputChange({
													...opt,
													logicId: JSON.stringify(thisItem),
												}),
											onBlur: blurHandler,
											disabled: props.mode !== "edit",
											invalid: isCellInvalid,
											isNumeric,
											formatSpec,
										})}
									</div>
								);
							})}
						</div>
					</Box>
				</Card.Body>
			</Card.Root>
		</div>
	);
};

export default TextfieldWithPeriod;
